import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Modal,
  Paper
} from "@mui/material";
import { Visibility, VisibilityOff, Lock, Person, MailOutline } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import registerIllustration from "./register.jpg";

const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

const Auth = () => {
  const [step, setStep] = useState("login"); // login, register, forgotPassword
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otp, setOtp] = useState(Array(4).fill(""));
  const [otpSent, setOtpSent] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [flip, setFlip] = useState(false); // flip state

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate(`/sell-my-property`);
  }, [navigate]);

  // Live validation
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(email && !emailRegex.test(email) ? "Invalid email" : "");
    const phoneRegex = /^[6-9]\d{9}$/;
    setPhoneError(phone && !phoneRegex.test(phone) ? "Invalid phone number" : "");
  }, [email, phone]);

  const otpValue = otp.join("");

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < otp.length - 1) document.getElementById(`otp-${index + 1}`)?.focus();
  };

  const passwordGuidelines = (
    <Typography variant="caption" color="textSecondary">
      Password must be at least 8 characters, include uppercase, lowercase, and a number or symbol.
    </Typography>
  );

  // --- API handlers ---
  const handleSendOtp = async () => {
    if (!email || emailError || (step === "register" && (!phone || phoneError))) {
      setMessage("Please enter valid details.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}api/request-otp`, {
        email,
        purpose: step === "forgotPassword" ? "forgotPassword" : "register",
      });
      setMessage(res.data.message + " Enter OTP to verify.");
      setOtpSent(true);
    } catch (err) {
      if (err.response) {
        if (err.response.status === 404) setMessage("User not found. Please register first.");
        else setMessage(err.response.data.message || "Error sending OTP");
      } else {
        setMessage("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!firstname || !lastname || !email || !phone || !password || !confirmPassword) {
      setMessage("All fields are required.");
      return;
    }
    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }
    if (otpValue.length < 4) {
      setMessage("Enter complete OTP.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}api/register`, {
        firstname,
        lastname,
        email,
        phone,
        password,
        otp: otpValue,
      });
      setMessage(res.data.message || "Registered successfully!");
      setStep("login");
      setOtpSent(false);
      setOtp(Array(4).fill(""));
      setFlip(false);
    } catch (err) {
      if (err.response) {
        if (err.response.status === 400) setMessage(err.response.data.message);
        else setMessage("Registration failed.");
      } else {
        setMessage("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setMessage("Email and Password are required.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}api/login`, { email, password });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      navigate(`/sell-my-property`);
      setMessage("Login successful!");
    } catch (err) {
      if (err.response) {
        if (err.response.status === 404) setMessage("User not found. Please register first.");
        else setMessage(err.response.data.message || "Login failed.");
      } else {
        setMessage("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!newPassword || otpValue.length < 4) {
      setMessage("Enter OTP and new password.");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}api/forgot-password`, { email, otp: otpValue, newPassword });
      setMessage(res.data.message || "Password reset successfully!");
      setStep("login");
      setOtpSent(false);
      setOtp(Array(4).fill(""));
      setNewPassword("");
    } catch (err) {
      if (err.response) {
        if (err.response.status === 404) setMessage("User not found. Please register first.");
        else setMessage(err.response.data.message || "Password reset failed.");
      } else {
        setMessage("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOtpInputs = () => (
    <Box display="flex" justifyContent="center" gap={1} mt={1} flexWrap="nowrap">
      {otp.map((d, i) => (
        <TextField
          key={i}
          id={`otp-${i}`}
          type="text"
          inputProps={{
            maxLength: 1,
            style: { textAlign: "center", fontSize: "1.2rem", width: "50px", padding: "12px" },
          }}
          value={d}
          onChange={(e) => handleOtpChange(i, e.target.value)}
        />
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        background: "linear-gradient(to right, #e0f7fa, #f1f8e9)",
        p: 2,
      }}
    >
      {/* Flip card container */}
      <Box
        sx={{
          perspective: 1500,
          width: { xs: "100%", sm: 400, md: 450 },
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            transition: "transform 0.8s",
            transformStyle: "preserve-3d",
            transform: flip ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          {/* FRONT: LOGIN */}
          <Paper
            elevation={10}
            sx={{
              backfaceVisibility: "hidden",
              borderRadius: 3,
              p: 4,
              bgcolor: "#ffffff",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {message && (
              <Box mb={2} p={1.5} bgcolor="#ffebee" color="#b71c1c" borderRadius={1}>
                <Typography variant="body2">{message}</Typography>
              </Box>
            )}
            <Typography variant="h5" fontWeight="bold" textAlign="center" color="#00796b">
              <Lock sx={{ verticalAlign: "middle", mr: 1 }} />
              Login
            </Typography>
            <Typography
  variant="body2"
  textAlign="center"
  color="textSecondary"
  sx={{ mb: 2, fontStyle: 'italic', fontFamily: "'Inter', sans-serif" }}
>
  🔒 Your data is secure and encrypted.
</Typography>

            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleLogin}
              disabled={loading}
              sx={{ transition: "0.3s", ":hover": { transform: "scale(1.03)" } }}
            >
              {loading ? <CircularProgress size={24} /> : "Login"}
            </Button>
            <Typography variant="body2" textAlign="center">
              Don't have an account?{" "}
              <Typography
                component="span"
                color="primary"
                sx={{ cursor: "pointer", fontWeight: "light" }}
                onClick={() => setFlip(true)}
              >
                Register here
              </Typography>
            </Typography>
            <Typography
              variant="body2"
              textAlign="center"
              color="primary"
              sx={{ cursor: "pointer", fontWeight: "light" }}
              onClick={() => setStep("forgotPassword")}
            >
              Forgot Password?
            </Typography>
          </Paper>

          {/* BACK: REGISTER */}
          <Paper
            elevation={10}
            sx={{
              backfaceVisibility: "hidden",
              borderRadius: 3,
              p: 4,
              bgcolor: "#ffffff",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              transform: "rotateY(180deg)",
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
            }}
          >
            <Typography variant="h5" fontWeight="bold" textAlign="center" color="#00796b">
              <Person sx={{ verticalAlign: "middle", mr: 1 }} />
              Register
            </Typography>

            <Typography
  variant="body2"
  textAlign="center"
  color="textSecondary"
  sx={{ mb: 1, fontStyle: 'italic', fontFamily: "'Inter', sans-serif" }}
>
  🔒 Your personal information is safe with us. We never share your data.
</Typography>

            <TextField label="First Name" value={firstname} onChange={(e) => setFirstname(e.target.value)} fullWidth />
            <TextField label="Last Name" value={lastname} onChange={(e) => setLastname(e.target.value)} fullWidth />
            <TextField
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={!!phoneError}
              helperText={phoneError}
              fullWidth
            />
            <TextField
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendOtp}
              disabled={otpSent || !email || !phone || emailError || phoneError}
            >
              {loading ? <CircularProgress size={24} /> : otpSent ? "OTP Sent" : "Send OTP"}
            </Button>
            {otpSent && renderOtpInputs()}
            {otpSent && (
              <>
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {passwordGuidelines}
                <TextField
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleRegister}
                  disabled={loading}
                  sx={{ transition: "0.3s", ":hover": { transform: "scale(1.03)" } }}
                >
                  {loading ? <CircularProgress size={24} /> : "Register"}
                </Button>
              </>
            )}
            <Typography
              variant="body2"
              textAlign="center"
              color="primary"
              sx={{ cursor: "pointer", fontWeight: "light" }}
              onClick={() => setFlip(false)}
            >
              Already have an account? Login here
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Forgot Password Modal */}
      <Modal open={step === "forgotPassword"} onClose={() => setStep("login")}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 300, sm: 400 },
            bgcolor: "#ffffff",
            p: 4,
            borderRadius: 3,
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold" textAlign="center" color="#00796b">
            <Lock sx={{ verticalAlign: "middle", mr: 1 }} />
            Reset Password
          </Typography>
          <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
          {!otpSent && (
            <Button variant="contained" color="primary" onClick={handleSendOtp}>
              {loading ? <CircularProgress size={24} /> : "Send OTP"}
            </Button>
          )}
          {otpSent && renderOtpInputs()}
          {otpSent && (
            <TextField
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          {otpSent && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleForgotPassword}
              disabled={loading}
              sx={{ transition: "0.3s", ":hover": { transform: "scale(1.03)" } }}
            >
              {loading ? <CircularProgress size={24} /> : "Reset Password"}
            </Button>
          )}
          <Typography
            variant="body2"
            textAlign="center"
            color="primary"
            sx={{ cursor: "pointer", fontWeight: "light" }}
            onClick={() => setStep("login")}
          >
            Back to Login
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default Auth;
